/** Self-contained NEXUS pulsating orb for PDF generation popup tabs. */

import { getStoredTheme } from "../context/ThemeContext";

function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const ORB_ANIMATIONS = `
    @keyframes nexus-halo-breathe {
      0%, 100% { transform: scale(1); opacity: 0.85; }
      50% { transform: scale(1.08); opacity: 1; }
    }
    @keyframes nexus-orb-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes nexus-orb-spin-reverse {
      from { transform: rotate(360deg); }
      to { transform: rotate(0deg); }
    }
    @keyframes nexus-orb-pulse {
      0%, 100% { transform: scale(1); opacity: 0.9; }
      50% { transform: scale(1.06); opacity: 1; }
    }`;

const THEME_STYLES = {
  dark: `
    body {
      background: linear-gradient(135deg, #04080f 0%, #070e1c 50%, #05111f 100%);
      color: #94a3b8;
    }
    .nexus-report-loader-halo {
      border: 1px solid rgba(34, 211, 238, 0.25);
      background: radial-gradient(circle, rgba(34, 211, 238, 0.15) 0%, transparent 70%);
    }
    .nexus-report-loader-energy {
      border-top: 2px solid rgba(34, 211, 238, 0.7);
      border-bottom: 2px solid rgba(0, 79, 115, 0.6);
    }
    .nexus-report-loader-glow {
      background: radial-gradient(circle, rgba(34, 211, 238, 0.35) 0%, transparent 70%);
    }
    .nexus-report-loader-core {
      background: linear-gradient(145deg, #004f73, #0a7ea4);
      box-shadow: 0 0 16px rgba(34, 211, 238, 0.5);
    }
    .nexus-report-loader-label { color: #cbd5e1; }
    .nexus-report-loader-brand { color: rgba(34, 211, 238, 0.55); }
  `,
  light: `
    body {
      background: linear-gradient(135deg, #eef2f7 0%, #f8fafc 55%, #e8f0f7 100%);
      color: #64748b;
    }
    .nexus-report-loader-halo {
      border: 1px solid rgba(0, 79, 115, 0.18);
      background: radial-gradient(circle, rgba(0, 79, 115, 0.1) 0%, transparent 70%);
    }
    .nexus-report-loader-energy {
      border-top: 2px solid rgba(0, 79, 115, 0.55);
      border-bottom: 2px solid rgba(26, 127, 168, 0.45);
    }
    .nexus-report-loader-glow {
      background: radial-gradient(circle, rgba(0, 79, 115, 0.14) 0%, transparent 70%);
    }
    .nexus-report-loader-core {
      background: linear-gradient(145deg, #004f73, #1a7fa8);
      box-shadow: 0 0 14px rgba(0, 79, 115, 0.28);
    }
    .nexus-report-loader-label { color: #475569; }
    .nexus-report-loader-brand { color: rgba(0, 79, 115, 0.55); }
  `,
};

export function reportLoadingHtml(message = "Generating report…", theme) {
  const resolvedTheme = theme === "dark" || theme === "light" ? theme : getStoredTheme();
  const label = escapeHtml(message);
  const themeCss = THEME_STYLES[resolvedTheme] || THEME_STYLES.light;

  return `<!DOCTYPE html>
<html lang="en" data-theme="${resolvedTheme}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${label}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    ${ORB_ANIMATIONS}
    body {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1.35rem;
      font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
    }
    .nexus-report-loader-orb {
      position: relative;
      width: 4.5rem;
      height: 4.5rem;
      flex-shrink: 0;
    }
    .nexus-report-loader-orb > * {
      position: absolute;
      inset: 0;
      border-radius: 50%;
    }
    .nexus-report-loader-halo {
      animation: nexus-halo-breathe 3s ease-in-out infinite;
    }
    .nexus-report-loader-energy {
      inset: 2px;
      animation: nexus-orb-spin 6s linear infinite;
    }
    .nexus-report-loader-energy--2 {
      inset: 4px;
      opacity: 0.65;
      animation: nexus-orb-spin-reverse 4.5s linear infinite;
    }
    .nexus-report-loader-glow {
      inset: 6px;
      animation: nexus-orb-pulse 2.5s ease-in-out infinite;
    }
    .nexus-report-loader-core {
      inset: 8px;
      animation: nexus-orb-pulse 2.5s ease-in-out infinite;
    }
    .nexus-report-loader-shine {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(255,255,255,0.35) 0%, transparent 55%);
    }
    .nexus-report-loader-label {
      font-size: 0.875rem;
      letter-spacing: 0.05em;
      text-align: center;
      padding: 0 1.5rem;
    }
    .nexus-report-loader-brand {
      font-size: 0.625rem;
      font-weight: 600;
      letter-spacing: 0.2em;
      text-transform: uppercase;
    }
    ${themeCss}
  </style>
</head>
<body>
  <div class="nexus-report-loader-orb" aria-hidden="true">
    <div class="nexus-report-loader-halo"></div>
    <div class="nexus-report-loader-energy"></div>
    <div class="nexus-report-loader-energy nexus-report-loader-energy--2"></div>
    <div class="nexus-report-loader-glow"></div>
    <div class="nexus-report-loader-core"><div class="nexus-report-loader-shine"></div></div>
  </div>
  <p class="nexus-report-loader-label">${label}</p>
  <p class="nexus-report-loader-brand">5X NEXUS</p>
</body>
</html>`;
}

/** Open a blank tab with the pulsating orb loader (call synchronously on user click). */
export function openReportLoadingTab(message = "Generating report…", theme) {
  const win = window.open("", "_blank");
  if (win) {
    win.document.write(reportLoadingHtml(message, theme));
    win.document.close();
  }
  return win;
}
